import { useRef, useEffect, useState, useCallback } from "react";

/**
 * Lightweight signature pad — HTML5 canvas, mouse + touch support.
 * Emits base64 PNG via onChange whenever the stroke set changes.
 */
export default function SignaturePad({ value, onChange, testId = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasContent, setHasContent] = useState(Boolean(value));

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1B3B2B";
    // restore previous signature
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
    }
  }, [value]);

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    setHasContent(true);
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange && onChange(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
    onChange && onChange("");
  };

  return (
    <div className="relative" data-testid={testId}>
      <div className="border-2 border-dashed border-[#E5E0D8] rounded-sm bg-white h-44 sm:h-48 relative hover:border-[#1B3B2B] transition-colors">
        <canvas
          ref={canvasRef}
          className="signature-canvas absolute inset-0 w-full h-full"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid={`${testId}-canvas`}
        />
        {!hasContent && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-[#8BA596]">
              <div className="font-serif-display text-xl italic">Sign here</div>
              <div className="text-xs uppercase tracking-widest mt-1">Click & drag — or use touch</div>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-[#4B5563]">By signing, you affirm this is your legal signature.</span>
        <button
          type="button"
          onClick={clear}
          className="text-xs uppercase tracking-widest text-[#CD7F32] hover:text-[#B06A26] font-semibold"
          data-testid={`${testId}-clear`}
        >
          Clear
        </button>
      </div>
    </div>
  );
}
